<template>
    <div>
        <HamletStaticTable
            :columns="columns"
            :items="items"
            :striped="false"
            :bordered="true"
            empty-mode="message"
            card-class="pt-0 mt-0"
            @onRowClick="openItem"
        >
            <template #header-label>
                {{ $t('Component(VerificationDocuments):label') }}
            </template>

            <template #cell-status="{ item }">
                <b-badge :variant="statusVariant(item.status)">
                    {{ item.status || '-' }}
                </b-badge>
            </template>

            <template #cell-type="{ item }">
                {{ translatedDocumentType(item) }}
            </template>

            <template #cell-relevance="{ item }">
                <b-badge :variant="relevanceVariant(item)">
                    {{ relevanceValue(item) }}
                </b-badge>
            </template>
        </HamletStaticTable>

        <ReviewWorkspaceHost
            :visible="showReviewItemModal"
            :process-id="process.processId"
            :item-id="selectedItem ? selectedItem.id : null"
            profile="research-review-document-item"
            :review-context="reviewWorkspaceContext"
            @close="closeItem"
            @item-saved="onWorkspaceItemSaved"
        />
    </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import { HamletStaticTable } from '@core/components/common'
import { ProductApi } from '@core/api'
import ReviewWorkspaceHost from '../../workspace/ReviewWorkspaceHost.vue'
import { ReviewProductLookupMode } from '../../workspace/product-lookup/review-product-lookup'

export default {
    name: 'ReviewableDocumentCompliance',
    components: {
        BBadge,
        HamletStaticTable,
        ReviewWorkspaceHost,
    },
    props: {
        process: {
            type: Object,
            required: true,
        },
        reviewCapabilities: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            localItems: [],
            showReviewItemModal: false,
            selectedItem: null,
        }
    },
    computed: {
        supplierId() {
            const metadata = this.process && this.process.externalContext && this.process.externalContext.metadata
                ? this.process.externalContext.metadata
                : {}

            return metadata.supplierId || null
        },
        items() {
            return this.localItems
        },
        columns() {
            return [
                { key: 'status', label: this.$t('Component(ComplianceOverview):column-status') },
                { key: 'type', label: this.$t('Component(Grid):column-type') },
                { key: 'relevance', label: this.$t('Component(Grid):column-relevance') },
            ]
        },
        reviewWorkspaceContext() {
            return {
                productLookup: {
                    mode: this.supplierId
                        ? ReviewProductLookupMode.SUPPLIER_PRODUCTS
                        : ReviewProductLookupMode.ORGANIZATION_PRODUCTS,
                    supplierId: this.supplierId,
                    search: this.searchReviewProducts,
                },
            }
        },
    },
    watch: {
        process: {
            immediate: true,
            deep: true,
            handler(value) {
                this.localItems = value && Array.isArray(value.items)
                    ? JSON.parse(JSON.stringify(value.items))
                    : []
            },
        },
    },
    methods: {
        payloadForDisplay(item) {
            if (!item || typeof item !== 'object') {
                return {}
            }

            if (item.effectivePayload && typeof item.effectivePayload === 'object') {
                return item.effectivePayload
            }

            if (item.reviewedPayload && typeof item.reviewedPayload === 'object') {
                return item.reviewedPayload
            }

            if (item.rawPayload && typeof item.rawPayload === 'object') {
                return item.rawPayload
            }

            return {}
        },
        openItem(item) {
            this.selectedItem = item
            this.showReviewItemModal = true
        },
        closeItem() {
            this.showReviewItemModal = false
            this.selectedItem = null
        },
        onItemSaved(savedItem) {
            const index = this.localItems.findIndex(item => item.id === savedItem.id)
            if (index === -1) {
                return
            }

            this.$set(this.localItems, index, savedItem)
            this.$emit('item-saved', savedItem)
        },
        onWorkspaceItemSaved(savedItem) {
            this.onItemSaved(savedItem)
            this.closeItem()
        },
        searchReviewProducts({ mode, supplierId = null, query = '' }) {
            if (mode === ReviewProductLookupMode.SUPPLIER_PRODUCTS && supplierId) {
                return ProductApi.searchPurchasedProducts(supplierId, query)
                    .then(response => (Array.isArray(response.data) ? response.data : []))
            }

            if (mode === ReviewProductLookupMode.ORGANIZATION_PRODUCTS) {
                return ProductApi.searchProducts(query)
                    .then(response => (
                        response.data && Array.isArray(response.data.items)
                            ? response.data.items
                            : []
                    ))
            }

            return Promise.resolve([])
        },
        statusVariant(status) {
            if (status === 'reviewed') {
                return 'success'
            }

            if (status === 'pending') {
                return 'warning'
            }

            return 'secondary'
        },
        documentTypeValue(item) {
            const payload = this.payloadForDisplay(item)
            const type = payload && payload.documentInformation
                ? payload.documentInformation.type
                : null

            return type || null
        },
        translatedDocumentType(item) {
            const type = this.documentTypeValue(item)
            if (!type) {
                return '-'
            }

            return this.$t(`Component(VerificationDocumentTypeEnum):${type}`)
        },
        relevanceValue(item) {
            const payload = this.payloadForDisplay(item)
            const relevance = payload && payload.documentInformation
                ? payload.documentInformation.relevance
                : null

            return relevance || '-'
        },
        relevanceVariant(item) {
            const payload = this.payloadForDisplay(item)
            const relevance = payload && payload.documentInformation
                ? payload.documentInformation.relevance
                : null

            const map = {
                relevant: 'success',
                uncertain: 'warning',
                irrelevant: 'danger',
            }

            return map[relevance] || 'secondary'
        },
    },
}
</script>
